/* calendar-drawer.jsx — create / edit / delete a meeting in the right-side drawer. */
function MtField({ label, children, hint }) {
  return <div className="field"><label>{label}</label>{children}{hint && <span className="hint">{hint}</span>}</div>;
}

function AttendeePicker({ attendees, onAdd }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const has = (type, id) => attendees.some((a) => a.type === type && a.id === id);
  const add = (type, id) => { onAdd({ type, id }); };

  return (
    <div className="mt-picker" ref={ref}>
      <button type="button" className="btn ghost sm" onClick={() => setOpen((v) => !v)}><Icon name="plus" size={13} />Add attendee</button>
      {open && (
        <div className="mt-picker-menu">
          <div className="mt-picker-group">Employees</div>
          {CAL.employees.map((e) => (
            <button key={e.id} type="button" className="mt-picker-item" disabled={has("employee", e.id)} onClick={() => add("employee", e.id)}>
              <Avatar name={e.name} avatarKey={e.avatarKey} />
              <span className="meta"><div className="nm">{e.name}</div><div className="rl">{e.role}</div></span>
              {has("employee", e.id) && <span className="added">Added</span>}
            </button>
          ))}
          {CAL.customers.map((c) => (
            <React.Fragment key={c.id}>
              <div className="mt-picker-group">{c.name} · contacts</div>
              {(c.contactPersons || []).map((k) => {
                const full = [k.name, k.lastName].filter(Boolean).join(" ");
                return (
                  <button key={k.id} type="button" className="mt-picker-item" disabled={has("contact", k.id)} onClick={() => add("contact", k.id)}>
                    <Avatar name={full} />
                    <span className="meta"><div className="nm">{full}</div><div className="rl">{k.role}</div></span>
                    {has("contact", k.id) && <span className="added">Added</span>}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function normalizeMeetingJoinLink(rawLink) {
  const trimmed = String(rawLink || "").trim();
  if (!trimmed) return { href: "", teamsDeepLink: false };

  const withProtocol = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withProtocol);
    const protocol = url.protocol.toLowerCase();
    if (!["http:", "https:", "msteams:"].includes(protocol)) return { href: "", teamsDeepLink: false };

    const isTeamsDeepLink =
      url.hostname.toLowerCase() === "teams.microsoft.com" &&
      url.pathname.toLowerCase().startsWith("/l/");
    if (isTeamsDeepLink && protocol !== "msteams:") {
      url.protocol = "msteams:";
      return { href: url.href, teamsDeepLink: true };
    }

    return { href: url.href, teamsDeepLink: protocol === "msteams:" && isTeamsDeepLink };
  } catch {
    return { href: "", teamsDeepLink: false };
  }
}

function MeetingDrawer({ meeting, isNew, scopeProjectId, emailReady = false, sendingLink = false, onClose, onSave, onDelete, onSendLink, onIngestTranscript }) {
  const [f, setF] = React.useState(meeting);
  const [ingestOpen, setIngestOpen] = React.useState(false);
  const [ingestEmployeeId, setIngestEmployeeId] = React.useState("");
  const [ingestBusy, setIngestBusy] = React.useState(false);
  React.useEffect(() => { setF(meeting); }, [meeting.id]);
  React.useEffect(() => {
    setIngestOpen(false);
    setIngestBusy(false);
    setIngestEmployeeId("");
  }, [meeting.id]);
  const set = (k, v) => setF((cur) => ({ ...cur, [k]: v }));
  const generateJitsiLink = () => {
    const titleSlug = String(f.title || "meeting")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "meeting";
    const token = window.crypto && window.crypto.randomUUID
      ? window.crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
    set("videoLink", `https://meet.jit.si/appligator-${f.date || "meeting"}-${titleSlug}-${token}`);
  };

  const addAttendee = (a) => setF((cur) => cur.attendees.some((x) => x.type === a.type && x.id === a.id) ? cur : ({ ...cur, attendees: [...cur.attendees, a] }));
  const removeAttendee = (a) => setF((cur) => ({ ...cur, attendees: cur.attendees.filter((x) => !(x.type === a.type && x.id === a.id)) }));
  const addResult = () => setF((cur) => ({
    ...cur,
    results: [...(cur.results || []), { clientId: "r" + Math.random().toString(36).slice(2, 9), type: "Action", text: "" }],
  }));
  const updateResult = (key, patch) => setF((cur) => ({
    ...cur,
    results: (cur.results || []).map((result) => (result.clientId || result.id) === key ? { ...result, ...patch } : result),
  }));
  const removeResult = (key) => setF((cur) => ({
    ...cur,
    results: (cur.results || []).filter((result) => (result.clientId || result.id) !== key),
  }));

  const titleOk = (f.title || "").trim().length > 0;
  const timeOk = CAL.minutesOf(f.end) > CAL.minutesOf(f.start);
  const valid = titleOk && timeOk;
  const linkedProjectId = f.projectId || scopeProjectId || "";
  const linkedProject = linkedProjectId ? CAL.projectById[linkedProjectId] : null;
  const aiEmployees = CAL.employees.filter((employee) => employee.type === "Ai" && employee.active !== false);
  const ingestEmployee = aiEmployees.find((employee) => employee.id === ingestEmployeeId) || aiEmployees[0] || null;
  const hasTranscript = !!(f.transcript || "").trim();
  const canIngestTranscript = !isNew && valid && !!linkedProjectId && hasTranscript && !!onIngestTranscript && !!ingestEmployee && !ingestBusy;
  const joinLink = normalizeMeetingJoinLink(f.videoLink);
  const canJoin = !!joinLink.href;
  const joinTitle = canJoin
    ? joinLink.teamsDeepLink ? "Open in Microsoft Teams" : "Open meeting link"
    : "Add a valid meeting link before joining.";
  const canSendLink = !isNew && valid && !!f.videoLink && emailReady && !!onSendLink && !sendingLink;
  const sendLinkTitle = isNew
    ? "Save the meeting before sending the link."
    : !emailReady
      ? "Set up the email account in settings to send meeting links."
      : !f.videoLink
        ? "Add or generate a video call link before sending."
        : !valid
          ? "Fix the meeting details before sending."
          : "Send meeting link to attendees";
  const ingestTitle = isNew
    ? "Save the meeting before ingesting the transcript."
    : !linkedProjectId
      ? "Link the meeting to a project before ingesting the transcript."
      : !hasTranscript
        ? "Paste a transcript before ingesting it."
        : aiEmployees.length === 0
          ? "No active AI employees are available."
          : !valid
            ? "Fix the meeting details before ingesting the transcript."
            : "Ask an AI employee to extract project takeaways into one follow-up";

  const submit = () => { if (valid) onSave({ ...f, title: f.title.trim() }); };
  const sendLink = () => { if (canSendLink) onSendLink({ ...f, title: f.title.trim() }); };
  const openIngestTranscript = () => {
    if (!canIngestTranscript) return;
    setIngestEmployeeId((cur) => cur || (ingestEmployee && ingestEmployee.id) || "");
    setIngestOpen(true);
  };
  const ingestTranscript = async () => {
    const employee = aiEmployees.find((item) => item.id === ingestEmployeeId) || ingestEmployee;
    if (!canIngestTranscript || !employee) return;
    setIngestBusy(true);
    try {
      await onIngestTranscript({ ...f, title: f.title.trim(), projectId: linkedProjectId }, employee);
      setIngestOpen(false);
    } finally {
      setIngestBusy(false);
    }
  };
  const joinMeeting = () => {
    if (!canJoin) return;
    window.open(joinLink.href, "_blank", "noopener,noreferrer");
  };

  return (
    <React.Fragment>
      <div className="overlay" onClick={onClose} />
      <aside className="drawer meeting-drawer" role="dialog" aria-label={isNew ? "New meeting" : "Edit meeting"}>
        <header>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2>{isNew ? "New meeting" : "Edit meeting"}</h2>
            <div className="sub">{CAL.fmtDayLabel(f.date)} · {CAL.fmtTime(f.start)}–{CAL.fmtTime(f.end)}</div>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><Icon name="close" size={17} /></button>
        </header>

        <div className="body meeting-body">
          <div className="meeting-main">
            <MtField label="Title" hint={!titleOk ? "A title is required." : null}>
              <input className="inp" autoFocus value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Orchard kickoff" />
            </MtField>

            <div className="field-row">
              <MtField label="Type">
                <select className="inp" value={f.type} onChange={(e) => set("type", e.target.value)}>
                  {CAL.MEETING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </MtField>
              <MtField label="Status">
                <select className="inp" value={f.status} onChange={(e) => set("status", e.target.value)}>
                  {CAL.MEETING_STATUSES.map((s) => <option key={s} value={s}>{s === "unprocessed" ? "Unprocessed" : s}</option>)}
                </select>
              </MtField>
            </div>

            <MtField label="Date">
              <input className="inp" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} />
            </MtField>
            <div className="field-row">
              <MtField label="Start">
                <input className="inp" type="time" value={f.start} onChange={(e) => set("start", e.target.value)} />
              </MtField>
              <MtField label="End" hint={!timeOk ? "End must be after start." : null}>
                <input className="inp" type="time" value={f.end} onChange={(e) => set("end", e.target.value)} />
              </MtField>
            </div>

            {!scopeProjectId && (
              <MtField label="Linked project" hint="Optional — leave as internal for non-client meetings.">
                <select className="inp" value={f.projectId} onChange={(e) => set("projectId", e.target.value)}>
                  <option value="">Internal · no project</option>
                  {CAL.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </MtField>
            )}

            <MtField label="Video call link">
              <div className="input-action-row">
                <input className="inp" value={f.videoLink} onChange={(e) => set("videoLink", e.target.value)} placeholder="https://..." />
                <button type="button" className="btn ghost sm" disabled={!canJoin} title={joinTitle} onClick={joinMeeting}>
                  <Icon name="external" size={13} />Join
                </button>
                <button type="button" className="btn ghost sm" onClick={generateJitsiLink}><Icon name="video" size={13} />Jitsi</button>
                <span className="meeting-send-link-wrap" title={sendLinkTitle}>
                  <button type="button" className="btn ghost sm" disabled={!canSendLink} onClick={sendLink}>
                    <Icon name={sendingLink ? "refresh" : "mail"} size={13} />{sendingLink ? "Sending..." : "Send link"}
                  </button>
                </span>
              </div>
            </MtField>

            <div className="field">
              <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span>Attendees</span>
                <span className="hint" style={{ fontWeight: 400 }}>{f.attendees.length} added</span>
              </label>
              <div className="mt-attendees">
                {f.attendees.length === 0 && <div className="mt-att-empty">No attendees yet — add employees and customer contacts.</div>}
                {f.attendees.map((a, i) => (
                  <div key={a.type + a.id + i} className="mt-att-row">
                    <Avatar name={CAL.attendeeLabel(a)} avatarKey={CAL.attendeeAvatarKey(a)} />
                    <span className="meta"><div className="nm">{CAL.attendeeLabel(a)}</div><div className="rl">{CAL.attendeeMeta(a)}</div></span>
                    <span className={`mt-att-kind ${a.type === "employee" ? "emp" : "con"}`}>{a.type === "employee" ? "Team" : "Contact"}</span>
                    <button className="icon-btn" title="Remove" onClick={() => removeAttendee(a)}><Icon name="close" size={15} /></button>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}><AttendeePicker attendees={f.attendees} onAdd={addAttendee} /></div>
            </div>

            <MtField label="Agenda / notes">
              <textarea className="inp" rows="5" value={f.agenda} onChange={(e) => set("agenda", e.target.value)} placeholder="Goals, talking points, decisions…" />
            </MtField>

            <MtField label="Transcript">
              <textarea className="inp" rows="6" value={f.transcript || ""} onChange={(e) => set("transcript", e.target.value)} placeholder="Paste the meeting transcript…" />
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span className="hint">{linkedProject ? `Linked to ${linkedProject.name}` : "Link a project to ingest transcript takeaways."}</span>
                <span title={ingestTitle}>
                  <button type="button" className="btn ghost sm" disabled={!canIngestTranscript} onClick={openIngestTranscript} data-appligator-id="meeting-ingest-transcript">
                    <Icon name="handover" size={13} />Ingest transcript
                  </button>
                </span>
              </div>
            </MtField>
          </div>

          <aside className="meeting-results-panel">
            <div className="meeting-results-head">
              <label>Follow-ups</label>
              <button type="button" className="btn ghost sm" onClick={addResult}><Icon name="plus" size={13} />Add follow-up</button>
            </div>
            <div className="mt-results-list">
              {(f.results || []).length === 0 && <div className="mt-att-empty">No follow-ups yet.</div>}
              {(f.results || []).map((result) => {
                const key = result.clientId || result.id;
                return (
                  <div key={key} className="mt-result-item">
                    <div className="mt-result-top">
                      <select className="inp" value={result.type || "Action"} onChange={(e) => updateResult(key, { type: e.target.value })}>
                        {CAL.MEETING_RESULT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                      </select>
                      <button type="button" className="icon-btn" title="Delete follow-up" onClick={() => removeResult(key)}><Icon name="trash" size={14} /></button>
                    </div>
                    <textarea className="inp" rows="3" value={result.text || ""} onChange={(e) => updateResult(key, { text: e.target.value })} placeholder="Outcome, action, remark..." />
                  </div>
                );
              })}
            </div>
          </aside>
        </div>

        <footer>
          {!isNew && <button className="btn danger" style={{ marginRight: "auto" }} onClick={() => onDelete(f.id)}><Icon name="trash" size={14} />Delete</button>}
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={!valid} onClick={submit}><Icon name="check" size={14} />{isNew ? "Create meeting" : "Save changes"}</button>
        </footer>
      </aside>
      {ingestOpen && (
        <React.Fragment>
          <div className="ai-compose-backdrop" onClick={() => !ingestBusy && setIngestOpen(false)} />
          <section className="ai-compose-modal" role="dialog" aria-modal="true" aria-labelledby="meeting-ingest-title">
            <header>
              <Icon name="handover" size={18} />
              <div>
                <h2 id="meeting-ingest-title">Ingest transcript</h2>
                <p>{linkedProject ? linkedProject.name : "Linked project"} · {f.title || "Meeting"}</p>
              </div>
              <button className="icon-btn" type="button" title="Close" onClick={() => !ingestBusy && setIngestOpen(false)} disabled={ingestBusy}><Icon name="close" size={16} /></button>
            </header>
            <div className="body">
              <MtField label="Employee">
                <select className="inp" value={ingestEmployeeId || (ingestEmployee && ingestEmployee.id) || ""} onChange={(event) => setIngestEmployeeId(event.target.value)} data-appligator-id="meeting-ingest-employee">
                  {aiEmployees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name} · {employee.role || "AI employee"}</option>)}
                </select>
              </MtField>
              <div className="ai-compose-reference">
                <span>Meeting follow-up</span>
                <strong>{f.title || "Untitled meeting"}</strong>
              </div>
            </div>
            <footer>
              <button className="btn ghost" type="button" onClick={() => setIngestOpen(false)} disabled={ingestBusy}>Cancel</button>
              <button className="btn" type="button" onClick={ingestTranscript} disabled={!canIngestTranscript} data-appligator-id="meeting-ingest-submit">
                <Icon name="send" size={14} />{ingestBusy ? "Sending..." : "Send to employee"}
              </button>
            </footer>
          </section>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

Object.assign(window, { MeetingDrawer });
